import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import styles from './styles.module.scss';

import { useGetLocationsQuery, useGetSensorsDatesQuery, useGetSensorsDailySummariesQuery } from '../../store/api/sensors';
import { selectDate, updateSorting } from '../../store/slices/sensors';
import { useSelectedDate, useViewport } from '../../hooks';
import { BreakpointMobile, BreakpointMobileUp } from '../../constants/ui';
import { useGetFieldsQuery } from '../../store/api/fields';
import { PhotosTypeSwitch } from '../PhotosTypeSwitch/index.jsx';
import { PageHeader } from '../PageLayout/index.jsx';
import CropsSelect from '../CropsSelect/index.jsx';
import { getWorkspaceCrops } from '../../utils/crops';
import { setWorkspaceCrops } from '../../store/slices/workspaces';
import { ScoringGrid, ScoringItemBody, ScoringLegend, ScoringPopup } from './index.jsx';
import { FilterDate, FilterOrderButton } from '../Filters/index.jsx';
import { Loading } from '../index.jsx';
import { SidebarItem } from '../Sidebar/index.jsx';
import SidebarFieldHeader from '../Sidebar/SidebarFieldHeader.jsx';
import SidebarTitle from '../Sidebar/SidebarTitle.jsx';
import { getSidebarLinksMap } from '../../utils/ui';
import RequestButton from '../GlobalComponents/RequestButton.jsx';

const Scoring = ({ t }) => {
  const SidebarLinksMap = getSidebarLinksMap(t);

  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { widthViewport } = useViewport();
  const [sensorsList, setSensorsList] = useState([]);
  const [cropsFilter, setCropsFilter] = useState([]);
  const [dirScoring, setDirScoring] = useState('asc');
  const [activePopup, setActivePopup] = useState(false);
  const [popupData, setPopupData] = useState({ left: 0, top: 0, bottom: 0, imageDay: '', imageNight: '', text: '' });

  const {
    selectedDate,
    sorting: { dir, parameter, crop },
  } = useSelector((state) => state.sensors);
  const { crops } = useSelector((state) => state.catalog);
  const { currentWorkspaceId, workspaceCrops } = useSelector((state) => state.workspaces);
  const { currentUserRole } = useSelector((state) => state.auth);

  const { data: dates = [] } = useGetSensorsDatesQuery({
    crop_id: crop === 'all' ? undefined : crop,
  });

  useSelectedDate(dates);

  const {
    data: sensors = [],
    isSuccess: isSensorsSuccess,
    isFetching: isSensorsLoading,
  } = useGetSensorsDailySummariesQuery(
    {
      date: selectedDate,
      crop_id: crop === 'all' ? undefined : crop,
      query: searchParams.get('search'),
    },
    {
      skip: !selectedDate,
      refetchOnMountOrArgChange: true,
    },
  );
  const {
    data: allSensors = [],
    isSuccess: isAllSensorsSuccess,
    isFetching: isAllSensorsLoading,
  } = useGetSensorsDailySummariesQuery(
    { date: selectedDate },
    {
      skip: !selectedDate,
      refetchOnMountOrArgChange: true,
    },
  );
  const { data: fields, isSuccess: isFieldsSuccess, isFetching: isFieldsLoading } = useGetFieldsQuery();

  useGetLocationsQuery({}, { skip: !currentWorkspaceId, refetchOnMountOrArgChange: true });

  const onChangeCrop = (value) => {
    if (value) {
      dispatch(updateSorting({ dir, parameter, crop: value.id }));
      if (value.id === 'all') {
        searchParams.delete('crop');
      } else {
        searchParams.set('crop', value.id);
      }
      setSearchParams(searchParams);
    }
  };

  const onChangeBBCHSort = () => {
    const sort = dirScoring === 'desc' || dirScoring === '' ? 'asc' : 'desc';
    setDirScoring(sort);
    // sortSensors({ sort, list: sensorsList });
  };

  const changeDate = (date) => {
    dispatch(selectDate(date));
    setSearchParams({ date: date });
  };

  const sortSensors = ({ sort = 'asc', listAi, listNoAi }) => {
    if (listAi.length) {
      listAi.map((item) => {
        if (sort === 'asc') {
          item.sensors.sort((a, b) => b.bbchAI.value - a.bbchAI.value);
        } else {
          item.sensors.sort((a, b) => a.bbchAI.value - b.bbchAI.value);
        }
      });
    }
    setSensorsList([...listAi, ...listNoAi]);
  };

  const onShowPopup = (data) => {
    setActivePopup(true);
    setPopupData(data);
  };

  const onHidePopup = () => {
    setActivePopup(false);
  };

  useEffect(() => {
    if (searchParams.get('tab')) {
      dispatch(updateSorting({ dir, parameter, crop: searchParams.get('tab') }));
    } else if (crop !== 'all') {
      searchParams.set('tab', crop);
    }
  }, []);

  // Get crops list
  useEffect(() => {
    if ((workspaceCrops && workspaceCrops.length) || !isFieldsSuccess || !crops) {
      return;
    }
    dispatch(setWorkspaceCrops(getWorkspaceCrops({ fields, crops })));
  }, [isFieldsSuccess, fields, crops]);

  useEffect(() => {
    if (!isSensorsSuccess || !isFieldsSuccess || !isAllSensorsSuccess || !workspaceCrops) {
      return;
    }

    const seasons = [];
    const listAi = [];
    const listNoAi = [];

    fields.map((obj) => {
      if (obj.seasons) {
        obj.seasons.map((item) => {
          seasons.push({
            ...item,
            field: obj.name,
            fieldId: obj.id,
          });
        });
      }
    });

    seasons.map((obj) => {
      const sensorsCurrent = sensors.filter((s) => s.location.fieldSeasonId === obj.id && !s.bbchAI);
      const sensorsCurrentAi = sensors.filter((s) => s.location.fieldSeasonId === obj.id && s.bbchAI);
      if (sensorsCurrent.length > 0) {
        listNoAi.push({
          ...obj,
          sensors: sensorsCurrent,
        });
      }
      if (sensorsCurrentAi.length > 0) {
        listAi.push({
          ...obj,
          sensors: sensorsCurrentAi,
        });
      }
    });

    // crops list for selected date
    const cropsList = [];
    allSensors.map((obj) => {
      const season = seasons.filter((s) => s.id === obj.location.fieldSeasonId)[0];
      if (season) {
        if (!cropsList.includes(season.crop_id)) {
          cropsList.push(season.crop_id);
        }
      }
    });
    const filteredCrops = workspaceCrops.filter((c) => cropsList.includes(c.id));
    setCropsFilter(filteredCrops);

    sortSensors({ sort: 'asc', listAi: listAi, listNoAi: listNoAi });
  }, [isFieldsSuccess, isAllSensorsSuccess, sensors, workspaceCrops, isSensorsSuccess]);

  if (isFieldsLoading || isSensorsLoading) {
    return <Loading />;
  }

  return (
    <>
      {widthViewport < BreakpointMobileUp && (
        <PageHeader title={SidebarLinksMap.scoring.title} text={SidebarLinksMap.scoring.description}></PageHeader>
      )}
      <div className={styles.scoringContainer}>
        {/*Header*/}
        {sensorsList && sensorsList.length > 0 && (
          <div className={styles.viewHeader}>
            <div className={styles.viewHeaderCorner}>
              {widthViewport > BreakpointMobile && (
                <CropsSelect
                  isFilter={true}
                  cropsList={cropsFilter}
                  currentCrop={crop}
                  changeCrop={onChangeCrop}
                  t={t}
                />
              )}
            </div>
            <div className={styles.viewHeaderBody}>
              <div className={styles.viewHeaderBodyColumn}>
                {dates.length > 0 && (
                  <FilterDate allDates={dates} selectedDate={selectedDate} selectDate={changeDate} />
                )}
              </div>
              {widthViewport > BreakpointMobile && <PhotosTypeSwitch isGrayView={true} />}
              <FilterOrderButton onChangeOrder={onChangeBBCHSort} isCustomOrder={true} t={t} />
            </div>
          </div>
        )}

        {/*Chart*/}
        <div className={`${styles.scoring} ${!sensorsList.length ? styles.scoringNo : ''}`}>
          {sensorsList && sensorsList.length > 0 && (
            <>
              {/*Grid Legend*/}
              <ScoringLegend />

              {/*Grid title*/}
              <div className={styles.scoringListTitle}>
                <SidebarTitle title={t('fields.title')} count={sensorsList.length} />
              </div>

              {/*Grid*/}
              <ScoringGrid />

              {/*List*/}
              <div className={`${styles.scoringList} custom-scroll`}>
                {sensorsList.map((season, index) => {
                  return (
                    <div key={season.id + index + 'scoring'} className={styles.scoringItemWrapper}>
                      <div className={styles.scoringItemTitle}>
                        <SidebarFieldHeader
                          title={season.field}
                          crop={season.crop_id}
                          startDate={season.sowing_date}
                          endDate={season.harvest_date}
                          t={t}
                        />
                      </div>
                      {season.sensors.map((sensor, index) => {
                        return (
                          <div className={styles.scoringItem} key={sensor.id + index + 'scoring'}>
                            {widthViewport > BreakpointMobile && (
                              <div className={styles.scoringItemName}>
                                <SidebarItem title={sensor.location.name} t={t} />
                              </div>
                            )}
                            <ScoringItemBody
                              t={t}
                              crops={crops}
                              selectedDate={selectedDate}
                              sensor={sensor}
                              season={season}
                              onHidePopup={onHidePopup}
                              onShowPopup={onShowPopup}
                            ></ScoringItemBody>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/*Empty*/}
          {!sensorsList.length && !isSensorsLoading && !isAllSensorsLoading && (
            <div className={styles.scoringEmpty}>
              <div className={styles.viewEmpty}>
                <div className={styles.viewEmptyIcon}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="160" height="160" viewBox="0 0 160 160" fill="none">
                    <path
                      fill="var(--neutral-3)"
                      d="M75.765 2.161c2.088-2.881 6.382-2.881 8.47 0l5.982 8.254a5.23 5.23 0 0 0 6.973 1.388l8.685-5.337c3.032-1.864 6.999-.22 7.825 3.241l2.368 9.915a5.23 5.23 0 0 0 5.912 3.95l10.066-1.607c3.514-.561 6.55 2.475 5.989 5.99l-1.607 10.066a5.23 5.23 0 0 0 3.95 5.911l9.915 2.368c3.461.826 5.104 4.793 3.241 7.825l-5.336 8.685a5.23 5.23 0 0 0 1.387 6.973l8.254 5.982c2.881 2.088 2.881 6.382 0 8.47l-8.254 5.982a5.23 5.23 0 0 0-1.387 6.973l5.336 8.685c1.863 3.032.22 6.999-3.241 7.825l-9.915 2.368a5.23 5.23 0 0 0-3.95 5.912l1.607 10.066c.561 3.514-2.475 6.55-5.989 5.989l-10.066-1.607a5.23 5.23 0 0 0-5.912 3.95l-2.368 9.915c-.826 3.461-4.793 5.105-7.825 3.241l-8.685-5.336a5.23 5.23 0 0 0-6.973 1.387l-5.982 8.254c-2.088 2.881-6.382 2.881-8.47 0l-5.982-8.254a5.23 5.23 0 0 0-6.973-1.387l-8.685 5.336c-3.032 1.864-6.999.22-7.825-3.241l-2.368-9.915a5.23 5.23 0 0 0-5.912-3.95l-10.066 1.607c-3.514.561-6.55-2.475-5.989-5.989l1.607-10.066a5.23 5.23 0 0 0-3.95-5.912L9.707 113.7c-3.461-.826-5.105-4.793-3.241-7.825l5.337-8.685a5.23 5.23 0 0 0-1.388-6.973l-8.254-5.982c-2.881-2.088-2.881-6.382 0-8.47l8.254-5.982a5.23 5.23 0 0 0 1.388-6.973l-5.337-8.685c-1.864-3.032-.22-6.999 3.241-7.825l9.915-2.368a5.23 5.23 0 0 0 3.95-5.912l-1.607-10.066c-.561-3.514 2.475-6.55 5.99-5.989l10.066 1.607a5.23 5.23 0 0 0 5.911-3.95L46.3 9.707c.826-3.461 4.793-5.105 7.825-3.241l8.685 5.337a5.23 5.23 0 0 0 6.973-1.388l5.982-8.254Z"
                    />
                    <path
                      fill="var(--neutral-1)"
                      d="M108.3 65.066a.631.631 0 0 1 0-.267.573.573 0 0 1 0-.233v-.3l-.2-.5a1.586 1.586 0 0 0-.3-.366l-.3-.267h-.167l-13.133-8.3-12.4-7.667a2.832 2.832 0 0 0-.967-.5h-.266a2.736 2.736 0 0 0-.9 0h-.334a3.767 3.767 0 0 0-1.1.434l-24.9 15.5-.3.233-.3.267-.333.233-.167.2-.2.5v.5a2.298 2.298 0 0 0 0 .666v29.1a3.333 3.333 0 0 0 1.567 2.834l25 15.467.5.2h.267a2.88 2.88 0 0 0 1.733 0h.267l.5-.2 24.8-15.234a3.334 3.334 0 0 0 1.566-2.833v-29.1s.067-.234.067-.367ZM80 53.9l5.933 3.666L67.3 69.1l-5.967-3.667L80 53.899Zm-3.333 50L58.333 92.7V71.4l18.334 11.333V103.9ZM80 76.866l-6.367-3.833 18.634-11.567 6.4 3.967L80 76.866ZM101.667 92.6 83.333 104V82.733l18.334-11.334v21.2Z"
                    />
                  </svg>
                </div>
                <p className={styles.viewEmptyText}>{t('empty.scoring')}</p>
                {(currentUserRole === 'owner' || currentUserRole === 'editor') && (
                  <div className={styles.viewEmptyNav}>
                    <RequestButton t={t} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/*Popup*/}
        {activePopup && <ScoringPopup data={popupData} />}
      </div>
    </>
  );
};

export default Scoring;
